import React from 'react'
import { useReducedMotion } from 'framer-motion'
import { useIsApple } from '../../../hooks/useIsApple.ts'
import styles from './AIAgentOrbLoader.module.scss'

export type AIAgentOrbLoaderStatus = 'idle' | 'thinking' | 'responding' | 'error' | 'offline'

interface AIAgentOrbLoaderProps {
  status?: AIAgentOrbLoaderStatus
  onClick?: () => void
  className?: string
}

const AIAgentOrbLoader: React.FC<AIAgentOrbLoaderProps> = ({
  status = 'idle',
  onClick,
  className,
}) => {
  const shouldReduceMotion = useReducedMotion()
  const isApple = useIsApple()
  const idSuffix = React.useId().replace(/:/g, '')
  const maskId = `loader-mask-${idSuffix}`
  const softGradId = `loader-soft-${idSuffix}`
  const orbGradId = `loader-orb-grad-${idSuffix}`

  const getAriaLabel = (): string => {
    switch (status) {
      case 'idle':
        return 'Asistente con IA - Listo'
      case 'thinking':
        return 'Asistente con IA - Pensando'
      case 'responding':
        return 'Asistente con IA - Respondiendo'
      case 'error':
        return 'Asistente con IA - Error'
      case 'offline':
        return 'Asistente con IA - Sin conexión'
      default:
        return 'Asistente con IA'
    }
  }

  const Wrapper = onClick ? 'button' : 'div'
  const wrapperProps = onClick
    ? {
        onClick,
        'aria-label': getAriaLabel(),
        disabled: status === 'offline',
        type: 'button' as const,
      }
    : { 'aria-label': getAriaLabel(), role: 'img' as const }

  const statusModifier = (status === 'error' || status === 'offline') ? styles[`loader_${status}`] : ''
  const appleModifier = isApple ? styles.loader_apple : ''

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      <Wrapper
        className={`${styles.loader} ${statusModifier} ${appleModifier} ${shouldReduceMotion ? styles.reducedMotion : ''}`}
        {...wrapperProps}
      >
        <svg width={100} height={100} viewBox="0 0 100 100" aria-hidden>
          <defs>
            {isApple && (
              <>
                <linearGradient id={orbGradId} x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                  <stop offset="30%" stopColor="var(--color-one)" />
                  <stop offset="70%" stopColor="var(--color-two)" />
                </linearGradient>
                <radialGradient id={softGradId} cx="50%" cy="50%" r="50%" fx="50%" fy="50%" gradientUnits="objectBoundingBox">
                  <stop offset="0%" stopColor="white" stopOpacity={1} />
                  <stop offset="40%" stopColor="white" stopOpacity={0.95} />
                  <stop offset="75%" stopColor="white" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="white" stopOpacity={0} />
                </radialGradient>
              </>
            )}
            <mask id={maskId}>
              <polygon points="0,0 100,0 100,100 0,100" fill="black" />
              {isApple ? (
                <>
                  <circle cx={55} cy={41.67} r={20} fill={`url(#${softGradId})`} />
                  <circle cx={50} cy={58.33} r={25} fill={`url(#${softGradId})`} />
                  <circle cx={42} cy={44} r={18} fill={`url(#${softGradId})`} />
                  <circle cx={58} cy={44} r={12} fill={`url(#${softGradId})`} />
                  <circle cx={42} cy={48} r={12} fill={`url(#${softGradId})`} />
                  <circle cx={58} cy={48} r={12} fill={`url(#${softGradId})`} />
                </>
              ) : (
                <>
                  <polygon points="25,25 75,25 50,75" fill="white" />
                  <polygon points="50,25 75,75 25,75" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                </>
              )}
            </mask>
          </defs>
          {isApple && (
            <rect x={0} y={0} width={100} height={100} fill={`url(#${orbGradId})`} mask={`url(#${maskId})`} />
          )}
        </svg>
        <div
          className={styles.box}
          style={{
            mask: `url(#${maskId})`,
            WebkitMask: `url(#${maskId})`,
          }}
        />
      </Wrapper>
    </div>
  )
}

export default AIAgentOrbLoader
